<template>
  <div class="dl-root">
    <main class="dl-home">
      <!-- 顶部标题栏 -->
      <header class="dl-header">
        <div class="dl-header-top">
          <div>
            <p class="dl-eyebrow">SOFTWARE DOWNLOADS</p>
            <h1 class="dl-title">系统下载统计</h1>
          </div>
          <span class="dl-header-icon">
            <di-icon icon="solar:chart-2-bold-duotone" :width="22" />
          </span>
        </div>
        <div class="dl-update">
          <span class="dl-dot"></span>
          <span>数据更新于 {{ updatedAt }}</span>
        </div>
      </header>

      <!-- 可滚动内容区 -->
      <section class="dl-scroll">
        <div class="dl-page">
          <!-- 今日累计下载 -->
          <div class="dl-total-row">
            <div>
              <p class="dl-label-sm">今日累计下载</p>
              <div class="dl-total-nums">
                <strong class="dl-total-value">{{ currentRange.todayNum }}</strong>
                <span class="dl-trend-up" :style="{color: currentRange.growth < 0 ? '#fb7185': '#6ee7b7'}">
                  <span :style="{transform: currentRange.growth < 0 ? 'rotate(180deg)' : ''}">
                    <di-icon icon="solar:arrow-up-linear" :width="13" />
                  </span>
                  {{ currentRange.growth }}%
                </span>
              </div>
            </div>
            <span class="dl-range-chip">{{ currentRange.label }}</span>
          </div>

          <!-- 下载量趋势图 -->
          <div class="dl-card">
            <div class="dl-card-head">
              <div>
                <h2 class="dl-card-title">下载量趋势</h2>
                <p class="dl-card-sub">所有下载方式累计</p>
              </div>
              <di-icon class="dl-icon-cyan" icon="solar:graph-up-linear" :width="19" />
            </div>
            <div class="dl-chart" ref="trendChart"></div>
          </div>

          <!-- 日均 / 峰值 -->
          <div class="dl-stat-grid">
            <div class="dl-stat-card">
              <div class="dl-stat-head">
                <span class="dl-stat-icon is-emerald"><di-icon icon="solar:calendar-mark-bold-duotone" :width="16" /></span>日均下载
              </div>
              <p class="dl-stat-value">{{ currentRange.average }}</p>
              <p class="dl-stat-note">较上周期 {{ currentRange.yoy_rate > 0 ? '+'+currentRange.yoy_rate :  currentRange.yoy_rate}}%</p>
            </div>
            <div class="dl-stat-card">
              <div class="dl-stat-head">
                <span class="dl-stat-icon is-amber"><di-icon icon="solar:fire-bold-duotone" :width="16" /></span>峰值日期
              </div>
              <p class="dl-stat-value">{{ currentRange.peak }}</p>
              <p class="dl-stat-note">{{ currentRange.peakCount }}次下载</p>
            </div>
          </div>

          <!-- 时间范围切换 -->
          <div class="dl-range-switch">
            <button
              v-for="item in rangeOptions"
              :key="item.key"
              type="button"
              :class="['dl-range-btn', { 'is-active': rangeKey === item.key }]"
              @click="switchRange(item.key)"
            >{{ item.label }}</button>
          </div>

          <!-- 下载方式分布 -->
          <div class="dl-section-head">
            <div>
              <h2 class="dl-section-title">下载方式分布</h2>
              <p class="dl-section-sub">{{ currentRange.label }}累计 · 共 {{ currentRange.total }} 次</p>
            </div>
            <button type="button" class="dl-link-btn" @click="toggleChannels">
              {{ channelToggleLabel }}
              <di-icon :icon="channelToggleIcon" :width="14" />
            </button>
          </div>
          <div class="dl-channel-panel">
            <div class="dl-channel-item" v-for="(item, index) in visibleChannels" :key="item.name">
              <div class="dl-channel-row">
                <span class="dl-channel-name">
                  <span class="dl-channel-dot" :style="{ backgroundColor: colorList[index] }"></span>{{ item.name }}
                </span>
                <strong class="dl-channel-nums">{{ item.count.num }}<span class="dl-channel-percent">{{ item.count.rate }}%</span></strong>
              </div>
              <div class="dl-channel-bar">
                <div class="dl-channel-bar-fill" :style="{ width: item.count.rate + '%', backgroundColor: colorList[index] }"></div>
              </div>
            </div>
          </div>

          <!-- 下载排行 -->
          <div class="dl-section-head">
            <div>
              <h2 class="dl-section-title">下载排行</h2>
              <p class="dl-section-sub">{{ currentRange.label }}累计排行</p>
            </div>
          </div>
          <div class="dl-rank-list">
            <!-- 若使用 vue-router，可把 a 换成 router-link :to="{ path: '/mirror-detail', query: { mirror: item.mirror } }" -->
            <div
              class="dl-rank-item"
              v-for="(item, index) in ranks"
              :key="item.file"
              @click.stop="toDetail(item.file)"
            >
              <span class="dl-rank-badge" :class="rankBadgeClass(index)">{{ index + 1 }}</span>
              <span class="dl-rank-body">
                <span class="dl-rank-name">{{ item.file }}</span>
                <span class="dl-rank-bar"><span class="dl-rank-bar-fill" :style="{ width: item.rate + '%', backgroundColor: colorList[index] }"></span></span>
              </span>
              <span class="dl-rank-count">
                <strong>{{ item.num }}</strong>
                <span class="dl-rank-count-unit">次下载</span>
              </span>
              <di-icon class="dl-arrow" icon="solar:alt-arrow-right-linear" :width="15" />
            </div>
          </div>

          <!-- 系统镜像 -->
          <div class="dl-section-head">
            <div>
              <h2 class="dl-section-title">系统镜像</h2>
              <p class="dl-section-sub">点击查看每个镜像下载方式明细</p>
            </div>
          </div>
          <div class="dl-mirror-list">
            <div
              class="dl-mirror-card"
              v-for="item in mirrors"
              :key="item.file"
              @click.stop="toDetail(item.file)"
            >
              <div class="dl-mirror-card-top">
                <div class="dl-mirror-card-left">
                  <span class="dl-mirror-logo"><di-icon icon="simple-icons:microsoft" :width="20"/></span>
                  <h3 class="dl-mirror-name">{{ item.file }}</h3>
                </div>
                <div class="dl-mirror-nums">
                  <strong>{{ item.num }}</strong>
                  <p
                    class="dl-mirror-delta"
                    :style="{color: item.yoy_rate < 0 ? '#fb7185': '#6ee7b7'}"
                  >
                    {{ item.yoy_rate > 0 ? '+'+item.yoy_rate : item.yoy_rate }}%
                  </p>
                  <div :class="[item.yoy_rate < 0 ? 'rotate-180' : '']" :style="{color: item.yoy_rate < 0 ? '#fb7185': '#6ee7b7'}">
                    <di-icon icon="solar:arrow-up-linear" :width="11" :height="11"/>
                  </div>
                  <div v-if="item.yoy_rate < 0" :style="{opacity: item.yoy_rate < 0 ? '0': ''}">
                    <di-icon icon="solar:arrow-up-linear" :width="11" :height="11"/>
                  </div>
                </div>
                <di-icon class="dl-arrow" icon="solar:alt-arrow-right-linear" :width="16" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import {getSystemStatisticsTopFilesYoy, getSystemStatisticsTopFiles, getSystemStatisticsAllSummary, getSystemStatisticsSummary} from "../../api/SoftwareDownloads";

/**
 * 图标数据（来自 Iconify 的 solar / simple-icons 图标集，已内联为 SVG path）
 * 新增图标时按 "前缀:名称": "SVG 内容" 追加即可，颜色跟随 currentColor。
 */
const ICONS = {
  "solar:chart-2-bold-duotone": "<g fill=\"currentColor\"><path d=\"M21 21.25C21.4142 21.25 21.75 21.5858 21.75 22C21.75 22.4142 21.4142 22.75 21 22.75H3C2.58579 22.75 2.25 22.4142 2.25 22C2.25 21.5858 2.58579 21.25 3 21.25H21Z\"/><path d=\"M5 9C5.94281 9 6.41414 9.00008 6.70703 9.29297C6.99992 9.58586 7 10.0572 7 11V17C7 17.9428 6.99992 18.4141 6.70703 18.707C6.41414 18.9999 5.94281 19 5 19C4.05719 19 3.58586 18.9999 3.29297 18.707C3.00008 18.4141 3 17.9428 3 17V11C3 10.0572 3.00008 9.58586 3.29297 9.29297C3.58586 9.00008 4.05719 9 5 9Z\"/><g opacity=\".5\"><path d=\"M12 5C12.9428 5 13.4141 5.00008 13.707 5.29297C13.9999 5.58586 14 6.05719 14 7V17C14 17.9428 13.9999 18.4141 13.707 18.707C13.4141 18.9999 12.9428 19 12 19C11.0572 19 10.5859 18.9999 10.293 18.707C10.0001 18.4141 10 17.9428 10 17V7C10 6.05719 10.0001 5.58586 10.293 5.29297C10.5859 5.00008 11.0572 5 12 5Z\"/><path d=\"M19 2C19.9428 2 20.4141 2.00008 20.707 2.29297C20.9999 2.58586 21 3.05719 21 4V17C21 17.9428 20.9999 18.4141 20.707 18.707C20.4141 18.9999 19.9428 19 19 19C18.0572 19 17.5859 18.9999 17.293 18.707C17.0001 18.4141 17 17.9428 17 17V4C17 3.05719 17.0001 2.58586 17.293 2.29297C17.5859 2.00008 18.0572 2 19 2Z\"/></g></g>",
  "solar:arrow-up-linear": "<path fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"1.5\" d=\"M12 20L12 4M6 10L12 4L18 10\"/>",
  "solar:graph-up-linear": "<g fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-width=\"1.5\"><path d=\"M2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2C16.714 2 19.0711 2 20.5355 3.46447C22 4.92893 22 7.28595 22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12Z\"/><path stroke-linejoin=\"round\" d=\"M7 14L9.29289 11.7071C9.68342 11.3166 10.3166 11.3166 10.7071 11.7071L12.2929 13.2929C12.6834 13.6834 13.3166 13.6834 13.7071 13.2929L17 10M14.5 10H17V12.5\"/></g>",
  "solar:calendar-mark-bold-duotone": "<g fill=\"currentColor\"><path d=\"M16.5 15C17.3284 15 18 15.6716 18 16.5C18 17.3284 17.3284 18 16.5 18C15.6716 18 15 17.3284 15 16.5C15 15.6716 15.6716 15 16.5 15Z\"/><path d=\"M17.04 2C17.4575 2.00005 17.7959 2.31006 17.7959 2.69238V4.15039C19.2467 4.25678 20.1997 4.5174 20.8994 5.1582C21.599 5.79899 21.8838 6.67149 22 8V9H2V8C2.11617 6.67149 2.40096 5.79899 3.10059 5.1582C3.80033 4.5174 4.75327 4.25678 6.2041 4.15039V2.69238C6.2041 2.31006 6.54248 2.00005 6.95996 2C7.37748 2 7.71582 2.31003 7.71582 2.69238V4.08887C8.3864 4.07696 9.13864 4.07715 9.98438 4.07715H14.0156C14.8614 4.07715 15.6136 4.07696 16.2842 4.08887V2.69238C16.2842 2.31003 16.6225 2 17.04 2Z\"/><path d=\"M22 14V12C22 11.161 21.9873 9.66527 21.9744 9H2.00586C1.99296 9.66527 2.00564 11.161 2.00564 12V14C2.00564 17.7712 2.00564 19.6569 3.17688 20.8284C4.34813 22 6.23321 22 10.0034 22H14.0023C17.7724 22 19.6575 22 20.8288 20.8284C22 19.6569 22 17.7712 22 14Z\" opacity=\".5\"/></g>",
  "solar:fire-bold-duotone": "<g fill=\"currentColor\"><path d=\"M12.8324 21.8013C15.9583 21.1747 20 18.926 20 13.1112C20 7.8196 16.1267 4.29593 13.3415 2.67685C12.7235 2.31757 12 2.79006 12 3.50492V5.3334C12 6.77526 11.3938 9.40711 9.70932 10.5018C8.84932 11.0607 7.92052 10.2242 7.816 9.20388L7.73017 8.36604C7.6304 7.39203 6.63841 6.80075 5.85996 7.3946C4.46147 8.46144 3 10.3296 3 13.1112C3 20.2223 8.28889 22.0001 10.9333 22.0001C11.0871 22.0001 11.2488 21.9955 11.4171 21.9858C11.863 21.9296 11.4171 22.085 12.8324 21.8013Z\" opacity=\".5\"/><path d=\"M8 18.4442C8 21.064 10.1113 21.8742 11.4171 21.9858C11.863 21.9296 11.4171 22.085 12.8324 21.8013C13.871 21.4343 15 20.4922 15 18.4442C15 17.1465 14.1814 16.3459 13.5401 15.9711C13.3439 15.8564 13.1161 16.0008 13.0985 16.2273C13.0429 16.9454 12.3534 17.5174 11.8836 16.9714C11.4685 16.4889 11.2941 15.784 11.2941 15.3331V14.7439C11.2941 14.3887 10.9365 14.1533 10.631 14.3346C9.49507 15.0085 8 16.3949 8 18.4442Z\"/></g>",
  "solar:alt-arrow-right-linear": "<path fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"1.5\" d=\"M9 5L15 12L9 19\"/>",
  "solar:alt-arrow-up-linear": "<path fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"1.5\" d=\"M19 15L12 9L5 15\"/>",
  "solar:cup-star-bold-duotone": "<g fill=\"currentColor\"><g opacity=\".5\"><path d=\"M14.1803 18.6963C14.6569 18.6963 15.0672 19.0327 15.1608 19.5L15.4577 21.25H8.54262L8.8395 19.5C8.93305 19.0327 9.34337 18.6963 9.81997 18.6963H14.1803Z\"/><path d=\"M12.0006 2C13.784 2.00002 15.2536 2.15704 16.3776 2.34668C17.5161 2.53877 18.0855 2.63489 18.5612 3.2207C19.0368 3.80653 19.0118 4.43984 18.9616 5.70605C18.7808 10.259 17.7603 15.9998 12.0006 16C6.24069 16 5.22046 10.2591 5.03969 5.70605C4.98943 4.43991 4.96354 3.80651 5.43911 3.2207C5.91482 2.63481 6.48495 2.53881 7.62368 2.34668C8.74769 2.15705 10.2173 2 12.0006 2Z\"/></g><path d=\"M18 21.25C18.4142 21.25 18.75 21.5858 18.75 22C18.75 22.4142 18.4142 22.75 18 22.75H6C5.58579 22.75 5.25 22.4142 5.25 22C5.25 21.5858 5.58579 21.25 6 21.25H18Z\"/><path d=\"M12.75 18.6963H11.25V15.9658C11.4906 15.9885 11.7405 16 12 16C12.2594 16 12.5094 15.9885 12.75 15.9658V18.6963Z\"/><path d=\"M5.01758 5.08691C5.02225 5.27545 5.03012 5.48073 5.03906 5.70605C5.12737 7.93046 5.41648 10.4385 6.36035 12.4219L3.54297 10.8574C2.79055 10.4394 2.41422 10.23 2.20703 9.87793C2 9.52592 2.00002 9.0956 2 8.23535V8.16211C1.99997 7.11866 2.00009 6.59692 2.2832 6.2041C2.56635 5.81128 3.06173 5.64641 4.05176 5.31641L5 5L5.01758 5.08691Z\"/><path d=\"M19.9482 5.31641C20.9383 5.64642 21.4337 5.81126 21.7168 6.2041C21.9999 6.59692 22 7.11865 22 8.16211V8.23535C22 9.09558 22 9.52593 21.793 9.87793C21.5858 10.23 21.2094 10.4394 20.457 10.8574L17.6406 12.4219C18.5844 10.4385 18.8726 7.93036 18.9609 5.70605C18.9699 5.47994 18.9787 5.27404 18.9834 5.08496L19 5L19.9482 5.31641Z\"/><path d=\"M12 5C12.2841 5 12.4745 5.34079 12.8545 6.02246L12.9521 6.19922C13.0601 6.39281 13.1141 6.48982 13.1982 6.55371C13.2824 6.61762 13.388 6.64103 13.5977 6.68848L13.7881 6.73145C14.526 6.8984 14.8946 6.98237 14.9824 7.26465C15.07 7.5468 14.8191 7.84084 14.3164 8.42871L14.1855 8.58105C14.0426 8.74817 13.9716 8.83216 13.9395 8.93555C13.9074 9.03887 13.9179 9.15027 13.9395 9.37305L13.959 9.57617C14.035 10.3606 14.0732 10.7531 13.8438 10.9277C13.6139 11.1022 13.268 10.9431 12.5771 10.625L12.3984 10.543C12.2022 10.4526 12.104 10.4072 12 10.4072C11.896 10.4072 11.7978 10.4526 11.6016 10.543L11.4229 10.625C10.732 10.9431 10.3861 11.1022 10.1563 10.9277C9.92683 10.7531 9.965 10.3606 10.041 9.57617L10.0605 9.37305C10.0821 9.15027 10.0926 9.03887 10.0605 8.93555C10.0284 8.83216 9.95736 8.74817 9.81445 8.58105L9.68359 8.42871C9.18087 7.84084 8.93 7.5468 9.01758 7.26465C9.10536 6.98237 9.47401 6.8984 10.2119 6.73145L10.4023 6.68848C10.612 6.64103 10.7176 6.61762 10.8018 6.55371C10.8859 6.48982 10.9399 6.39281 11.0479 6.19922L11.1455 6.02246C11.5255 5.34079 11.7159 5 12 5Z\"/></g>",
  "solar:widget-5-bold-duotone": "<g fill=\"currentColor\"><g opacity=\".5\"><path d=\"M7.625 15C8.67337 15 9.19784 15.0001 9.61133 15.1777C10.1625 15.4146 10.6008 15.8688 10.8291 16.4404C11.0003 16.8692 11 17.4129 11 18.5C11 19.5871 11.0003 20.1308 10.8291 20.5596C10.6008 21.1312 10.1625 21.5854 9.61133 21.8223C9.19784 21.9999 8.67337 22 7.625 22H5.375C4.32663 22 3.80216 21.9999 3.38867 21.8223C2.83749 21.5854 2.39922 21.1312 2.1709 20.5596C1.99969 20.1308 2 19.5871 2 18.5C2 17.4129 1.99969 16.8692 2.1709 16.4404C2.39922 15.8688 2.83749 15.4146 3.38867 15.1777C3.80216 15.0001 4.32663 15 5.375 15H7.625Z\"/><path d=\"M17.5 11C19.6213 11 20.6818 11.0002 21.3408 11.6445C21.9998 12.2889 22 13.3262 22 15.4004V17.5996C22 19.6738 21.9998 20.7111 21.3408 21.3555C20.6818 21.9998 19.6213 22 17.5 22C15.3787 22 14.3182 21.9998 13.6592 21.3555C13.0002 20.7111 13 19.6738 13 17.5996V15.4004C13 13.3262 13.0002 12.2889 13.6592 11.6445C14.3182 11.0002 15.3787 11 17.5 11Z\"/></g><path d=\"M6.5 2C8.62132 2 9.68181 2.00017 10.3408 2.64453C10.9998 3.2889 11 4.32621 11 6.40039V8.59961C11 10.6738 10.9998 11.7111 10.3408 12.3555C9.68181 12.9998 8.62132 13 6.5 13C4.37868 13 3.31819 12.9998 2.65918 12.3555C2.00017 11.7111 2 10.6738 2 8.59961V6.40039C2 4.32621 2.00017 3.2889 2.65918 2.64453C3.31819 2.00017 4.37868 2 6.5 2Z\"/><path d=\"M18.625 2C19.6734 2 20.1978 2.00012 20.6113 2.17773C21.1625 2.41459 21.6008 2.8688 21.8291 3.44043C22.0003 3.86921 22 4.41293 22 5.5C22 6.58707 22.0003 7.13079 21.8291 7.55957C21.6008 8.1312 21.1625 8.58541 20.6113 8.82227C20.1978 8.99988 19.6734 9 18.625 9H16.375C15.3266 9 14.8022 8.99988 14.3887 8.82227C13.8375 8.58541 13.3992 8.1312 13.1709 7.55957C12.9997 7.13079 13 6.58707 13 5.5C13 4.41293 12.9997 3.86921 13.1709 3.44043C13.3992 2.8688 13.8375 2.41459 14.3887 2.17773C14.8022 2.00012 15.3266 2 16.375 2H18.625Z\"/></g>",
  "solar:layers-minimalistic-linear": "<g fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-width=\"1.5\"><path d=\"M4.97883 9.68508C2.99294 8.89073 2 8.49355 2 8C2 7.50645 2.99294 7.10927 4.97883 6.31492L7.7873 5.19153C9.77318 4.39718 10.7661 4 12 4C13.2339 4 14.2268 4.39718 16.2127 5.19153L19.0212 6.31492C21.0071 7.10927 22 7.50645 22 8C22 8.49355 21.0071 8.89073 19.0212 9.68508L16.2127 10.8085C14.2268 11.6028 13.2339 12 12 12C10.7661 12 9.77318 11.6028 7.7873 10.8085L4.97883 9.68508Z\"/><path d=\"M22 12C22 12 21.0071 12.8907 19.0212 13.6851L16.2127 14.8085C14.2268 15.6028 13.2339 16 12 16C10.7661 16 9.77318 15.6028 7.7873 14.8085L4.97883 13.6851C2.99294 12.8907 2 12 2 12\"/><path d=\"M22 16C22 16 21.0071 16.8907 19.0212 17.6851L16.2127 18.8085C14.2268 19.6028 13.2339 20 12 20C10.7661 20 9.77318 19.6028 7.7873 18.8085L4.97883 17.6851C2.99294 16.8907 2 16 2 16\"/></g>",
  "solar:info-circle-linear": "<g fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-width=\"1.5\"><circle cx=\"12\" cy=\"12\" r=\"10\"/><path d=\"M12 17V11\"/><path stroke-linejoin=\"round\" d=\"M12 8H12.0001\"/></g>",
  "simple-icons:microsoft": "<path fill=\"currentColor\" d=\"M0 0v11.408h11.408V0zm12.594 0v11.408H24V0zM0 12.594V24h11.408V12.594zm12.594 0V24H24V12.594z\"/>",
  "simple-icons:ubuntu": "<path fill=\"currentColor\" d=\"M17.61.455a3.41 3.41 0 0 0-3.41 3.41a3.41 3.41 0 0 0 3.41 3.41a3.41 3.41 0 0 0 3.41-3.41a3.41 3.41 0 0 0-3.41-3.41M12.92.8C8.923.777 5.137 2.941 3.148 6.451a5 5 0 0 1 .26-.007a4.9 4.9 0 0 1 2.585.737A8.32 8.32 0 0 1 12.688 3.6A4.94 4.94 0 0 1 13.723.834A11 11 0 0 0 12.92.8m9.226 4.994a4.9 4.9 0 0 1-1.918 2.246a8.36 8.36 0 0 1-.273 8.303a4.9 4.9 0 0 1 1.632 2.54a11.16 11.16 0 0 0 .559-13.089M3.41 7.932A3.41 3.41 0 0 0 0 11.342a3.41 3.41 0 0 0 3.41 3.409a3.41 3.41 0 0 0 3.41-3.41a3.41 3.41 0 0 0-3.41-3.41zm2.027 7.866a4.9 4.9 0 0 1-2.915.358a11.1 11.1 0 0 0 7.991 6.698a11.2 11.2 0 0 0 2.422.249a4.88 4.88 0 0 1-.999-2.85a9 9 0 0 1-.836-.136a8.3 8.3 0 0 1-5.663-4.32zm11.405.928a3.41 3.41 0 0 0-3.41 3.41a3.41 3.41 0 0 0 3.41 3.41a3.41 3.41 0 0 0 3.41-3.41a3.41 3.41 0 0 0-3.41-3.41\"/>",
  "simple-icons:apple": "<path fill=\"currentColor\" d=\"M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04c-2.04.027-3.91 1.183-4.961 3.014c-2.117 3.675-.546 9.103 1.519 12.09c1.013 1.454 2.208 3.09 3.792 3.039c1.52-.065 2.09-.987 3.935-.987c1.831 0 2.35.987 3.96.948c1.637-.026 2.676-1.48 3.676-2.948c1.156-1.688 1.636-3.325 1.662-3.415c-.039-.013-3.182-1.221-3.22-4.857c-.026-3.04 2.48-4.494 2.597-4.559c-1.429-2.09-3.623-2.324-4.39-2.376c-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83c-1.207.052-2.662.805-3.532 1.818c-.78.896-1.454 2.338-1.273 3.714c1.338.104 2.715-.688 3.559-1.701\"/>"
};

/**
 * 内联 SVG 图标组件（替代 iconify-icon，无需任何外部脚本）
 * 用法：<di-icon icon="solar:xxx" :width="20" />，颜色取 currentColor
 */
const DiIcon = {
  name: 'DiIcon',
  functional: true,
  props: {
    icon: { type: String, required: true },
    width: { type: [Number, String], default: 20 }
  },
  render(h, ctx) {
    const p = ctx.props;
    const size = typeof p.width === 'number' ? p.width : parseFloat(p.width) || 20;
    return h('svg', {
      class: 'di-icon',
      attrs: {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        width: size,
        height: size,
        'aria-hidden': 'true'
      },
      domProps: { innerHTML: ICONS[p.icon] || '' }
    });
  }
};

/**
 * 下载统计 · 总览（Vue2 版）
 *
 * 说明：
 * 1. Tailwind 已全部改写为原生 CSS（scoped），并做了响应式：移动端为全屏 App 布局，
 *    ≥768px 为居中宽栏布局（内容多列网格排列），不再固定 393×852。
 * 2. echarts 使用 npm 包直接 import；图标为内联 SVG（DiIcon），不依赖 CDN。
 * 3. 文字行高按原 Tailwind 设计语义设置（16px→24px、14px→20px、12px→16px，其余继承 1.5）。
 * 4. 当前为静态 mock 数据，后续接接口时替换 data 中的 ranges / channels / ranks / mirrors 即可。
 */
export default {
  name: 'SoftwareDownloads',
  components: { DiIcon },
  data() {
    return {
      updatedAt: '2026/9/4',
      rangeKey: 'seven_days',
      channelExpanded: false,
      channelToggled: false,
      colorList: ['#67e8f9', '#60a5fa', '#a78bfa', '#fcd34d', '#fb7185', '#bef264', '#fb923c', '#93c5fd', '#fdba74', '#e2e8f0'],
      ranges: {
        'seven_days': {
          label: '近 7 天',
          todayNum: '12,846',
          total: '12,846',
          growth: '18.6',
          average: '1,835',
          yoy_rate: '18.6',
          peak: '09/03',
          peakCount: '2,087',
          dates: ['08/29', '08/30', '08/31', '09/01', '09/02', '09/03', '09/04'],
          values: [1612, 1728, 1856, 1764, 1918, 2087, 1881],
          previousDay: [1526, 1612, 1728, 1856, 1764, 1918, 2087],
          previousWeek: [1398, 1466, 1548, 1612, 1698, 1762, 1745]
        },
        'thirty_days': {
          label: '近 30 天',
          todayNum: '12,846',
          total: '48,392',
          growth: '18.6',
          average: '1,613',
          yoy_rate: '18.6',
          peak: '08/26',
          peakCount: '2,241',
          dates: ['08/06', '08/11', '08/16', '08/21', '08/26', '08/31', '09/04'],
          values: [1180, 1436, 1522, 1768, 2241, 1856, 1881],
          previousDay: [1124, 1328, 1456, 1682, 2134, 1928, 1856],
          previousWeek: [1068, 1248, 1386, 1544, 1862, 1764, 1698]
        },
        'month': {
          label: '本月',
          todayNum: '12,846',
          total: '7,643',
          growth: '18.6',
          average: '1,909',
          yoy_rate: '18.6',
          peak: '09/03',
          peakCount: '2,087',
          dates: ['09/01', '09/02', '09/03', '09/04'],
          values: [1764, 1918, 2087, 1881],
          previousDay: [1856, 1764, 1918, 2087],
          previousWeek: [1612, 1698, 1762, 1745]
        }
      },
      channels: [],
      ranks: [],
      mirrors: []
    };
  },
  computed: {
    currentRange() {
      return this.ranges[this.rangeKey];
    },
    rangeOptions() {
      return Object.keys(this.ranges).map(key => ({ key: key, label: this.ranges[key].label }));
    },
    visibleChannels() {
      return this.channelExpanded ? this.channels : this.channels.filter(item => !item.extra);
    },
    channelToggleLabel() {
      if (this.channelExpanded) return '收起明细';
      return this.channelToggled ? '查看明细' : '展开';
    },
    channelToggleIcon() {
      return this.channelExpanded ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-right-linear';
    }
  },
  created() {
    // 非响应式实例属性，不放入 data，避免 Vue 深度遍历 echarts 实例
    this.chart = null;
    this.getSystemStatisticsSummaryHandle();
    this.getSystemStatisticsTopFilesHandle();
    this.getSystemStatisticsTopFilesYoyHandle();
    this.getSystemStatisticsAllSummaryHandle();
  },
  watch: {
    rangeKey() {
      this.getSystemStatisticsSummaryHandle();
      this.getSystemStatisticsTopFilesHandle();
      this.getSystemStatisticsTopFilesYoyHandle();
      this.getSystemStatisticsAllSummaryHandle();
    }
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    toDetail(file) {
      this.$router.push({ name: 'MirrorDetail', query: { file, range: this.rangeKey } });
    },
    /**
     * 下载次数（7天、30天、本月）
     */
    getSystemStatisticsSummaryHandle() {
      getSystemStatisticsSummary({
        range: this.rangeKey
      }).then(res => {
        this.channels = Object.entries(res.data.list).map(([name, count], index) => ({
          name,
          count,
          ...(index > 3 ? { extra: true } : {}),
        }))
        this.ranges[this.rangeKey].total = res.data.num
      })
    },
    /**
     * 系统下载次数（高到低，7天、30天、本月）
     */
    getSystemStatisticsTopFilesHandle() {
      getSystemStatisticsTopFiles({
        range: this.rangeKey
      }).then(res => {
        this.ranks = res.data
      })
    },
    /**
     * 系统下载次数（同比排序）
     */
    getSystemStatisticsTopFilesYoyHandle() {
      getSystemStatisticsTopFilesYoy({
        range: this.rangeKey
      }).then(res => {
        this.mirrors = res.data
      })
    },
    /**
     * 汇总曲线
     */
    getSystemStatisticsAllSummaryHandle() {
      getSystemStatisticsAllSummary({
        range: this.rangeKey
      }).then(res => {
        this.updatedAt = res.data.end_date
        this.ranges[this.rangeKey].todayNum = res.data.today.num
        this.ranges[this.rangeKey].total = res.data.num
        this.ranges[this.rangeKey].growth = res.data.today.yoy_rate
        this.ranges[this.rangeKey].average = res.data.daily_avg.num
        this.ranges[this.rangeKey].yoy_rate = res.data.daily_avg.yoy_rate
        this.ranges[this.rangeKey].peak = res.data.peak.date
        this.ranges[this.rangeKey].peakCount = res.data.peak.num
        this.ranges[this.rangeKey].dates = res.data.daily_list.map(item => item.date)
        this.ranges[this.rangeKey].values = res.data.daily_list.map(item => item.num)
        this.ranges[this.rangeKey].previousDay = res.data.daily_list.map(item => item.prev_num)
        this.ranges[this.rangeKey].previousWeek = res.data.daily_list.map(item => item.avg_num)
        this.renderChart();
      })
    },
    switchRange(key) {
      this.rangeKey = key;
    },
    toggleChannels() {
      this.channelExpanded = !this.channelExpanded;
      this.channelToggled = true;
    },
    rankBadgeClass(index) {
      if (index === 0) return 'is-gold';
      if (index === 1) return 'is-silver';
      return 'is-bronze';
    },
    initChart() {
      this.chart = echarts.init(this.$refs.trendChart);
      this.renderChart();
    },
    renderChart() {
      if (!this.chart) return;
      const item = this.currentRange;
      this.chart.setOption({
        animationDuration: 450,
        legend: { top: 2, right: 0, itemWidth: 9, itemHeight: 3, itemGap: 10, textStyle: { color: '#94a3b8', fontSize: 10 }, data: ['当前', '前 1 天', '近 7 天'] },
        grid: { left: 4, right: 4, top: 32, bottom: 4, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: item.dates, axisLine: { lineStyle: { color: '#26384e' } }, axisTick: { show: false }, axisLabel: { color: '#71839b', fontSize: 10, margin: 12 } },
        yAxis: { type: 'value', splitNumber: 3, axisLabel: { color: '#71839b', fontSize: 10 }, splitLine: { lineStyle: { color: 'rgba(148,163,184,0.09)' } }, axisLine: { show: false } },
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#15263c',
          borderColor: '#2a425f',
          textStyle: { color: '#e2e8f0', fontSize: 11 },
          formatter: params => params[0].axisValue + params.map(point => '<br/><span style="color:' + point.color + '">●</span> ' + point.seriesName + ' <b>' + point.value.toLocaleString() + '</b> 次').join('')
        },
        series: [
          { name: '当前', data: item.values, type: 'line', smooth: true, symbol: 'circle', symbolSize: 6, lineStyle: { width: 3, color: '#67e8f9' }, itemStyle: { color: '#67e8f9', borderColor: '#0d2538', borderWidth: 2 }, areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(103,232,249,.25)' }, { offset: 1, color: 'rgba(103,232,249,0)' }] } } },
          { name: '前 1 天', data: item.previousDay, type: 'line', smooth: true, symbol: 'none', lineStyle: { width: 2, type: 'dashed', color: '#a78bfa' } },
          { name: '近 7 天', data: item.previousWeek, type: 'line', smooth: true, symbol: 'none', lineStyle: { width: 2, type: 'dashed', color: '#fbbf24' } }
        ]
      });
    },
    handleResize() {
      if (this.chart) this.chart.resize();
    }
  }
};
</script>

<style scoped>
/* ===== 图标基础 ===== */
.di-icon {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}

/* ===== 基础布局（响应式：移动端全屏 App 壳 / PC 端居中宽栏） ===== */
.dl-root {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  background: #050b16;
}
.dl-home {
  position: relative;
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  min-height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #09111f;
  color: #f1f5f9;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  line-height: 1.5;
}
.dl-home,
.dl-home * {
  box-sizing: border-box;
}
.dl-home a {
  color: inherit;
  text-decoration: none;
}
.dl-home strong {
  font-weight: 700;
}

/* ===== 顶部标题栏 ===== */
.dl-header {
  flex-shrink: 0;
  padding: 20px 20px 16px;
  background: #0b1526;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.dl-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.dl-eyebrow {
  font-size: 11px;
  letter-spacing: 0.18em;
  color: rgba(103, 232, 249, 0.8);
  font-weight: 600;
}
.dl-title {
  margin-top: 4px;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: #f1f5f9;
}
.dl-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 16px;
  background: rgba(103, 232, 249, 0.1);
  border: 1px solid rgba(103, 232, 249, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #67e8f9;
  flex-shrink: 0;
}
.dl-update {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
  color: #94a3b8;
}
.dl-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #34d399;
  box-shadow: 0 0 10px rgba(52, 211, 153, 0.8);
}

/* ===== 滚动内容区 ===== */
.dl-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.dl-scroll::-webkit-scrollbar {
  display: none;
}
.dl-page {
  padding: 20px 20px 32px;
}

/* ===== 今日累计 ===== */
.dl-total-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}
.dl-label-sm {
  font-size: 14px;
  line-height: 20px;
  color: #94a3b8;
}
.dl-total-nums {
  margin-top: 4px;
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.dl-total-value {
  font-size: 34px;
  line-height: 40px;
  font-weight: 700;
  letter-spacing: -0.025em;
}
.dl-trend-up {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: #6ee7b7;
  white-space: nowrap;
}
.dl-range-chip {
  border-radius: 9999px;
  padding: 6px 12px;
  font-size: 11px;
  color: #a5f3fc;
  background: rgba(103, 232, 249, 0.1);
  border: 1px solid rgba(103, 232, 249, 0.1);
  white-space: nowrap;
}

/* ===== 通用卡片 ===== */
.dl-card {
  margin-top: 20px;
  border-radius: 24px;
  background: #101d31;
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1);
}
.dl-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  gap: 12px;
}
.dl-card-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
}
.dl-card-sub {
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
}
.dl-icon-cyan {
  color: #67e8f9;
}
.dl-chart {
  width: 100%;
  height: 224px;
}

/* ===== 日均 / 峰值 ===== */
.dl-stat-grid {
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.dl-stat-card {
  border-radius: 16px;
  background: #101d31;
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 16px;
}
.dl-stat-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
  color: #94a3b8;
}
.dl-stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dl-stat-icon.is-emerald {
  background: rgba(52, 211, 153, 0.1);
  color: #6ee7b7;
}
.dl-stat-icon.is-amber {
  background: rgba(251, 191, 36, 0.1);
  color: #fcd34d;
}
.dl-stat-value {
  margin-top: 12px;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}
.dl-stat-note {
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
}

/* ===== 时间范围切换 ===== */
.dl-range-switch {
  margin-top: 24px;
  display: flex;
  padding: 4px;
  border-radius: 16px;
  background: #101d31;
  border: 1px solid rgba(255, 255, 255, 0.07);
}
.dl-range-btn {
  flex: 1;
  border: 0;
  border-radius: 12px;
  padding: 10px 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #94a3b8;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s, color 0.2s;
}
.dl-range-btn.is-active {
  background: #67e8f9;
  color: #072032;
}

/* ===== 区块标题 ===== */
.dl-section-head {
  margin-top: 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.dl-section-title {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
}
.dl-section-sub {
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: #64748b;
}
.dl-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: #67e8f9;
  background: none;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  flex-shrink: 0;
  white-space: nowrap;
}
.dl-top3 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 16px;
  color: #fcd34d;
  white-space: nowrap;
}

/* ===== 下载方式分布 ===== */
.dl-channel-panel {
  margin-top: 16px;
  border-radius: 24px;
  background: #101d31;
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.dl-channel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  line-height: 16px;
}
.dl-channel-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.dl-channel-dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  flex-shrink: 0;
  background-color: #e2e8f0;
}
.dl-channel-nums {
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}
.dl-channel-percent {
  font-weight: 400;
  color: #64748b;
  margin-left: 6px;
}
.dl-channel-bar {
  margin-top: 8px;
  height: 6px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
}
.dl-channel-bar-fill {
  height: 100%;
  border-radius: 9999px;
  background-color: #e2e8f0;
}

/* ===== 下载排行 ===== */
.dl-rank-list {
  margin-top: 12px;
  border-radius: 24px;
  background: #101d31;
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 12px;
}
.dl-rank-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 16px;
  padding: 8px;
  transition: background 0.2s;
}
.dl-rank-item:hover,
.dl-rank-item:active {
  background: #142943;
}
.dl-rank-badge {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}
.dl-rank-badge.is-gold {
  background: rgba(252, 211, 77, 0.15);
  color: #fde68a;
}
.dl-rank-badge.is-silver {
  background: rgba(203, 213, 225, 0.1);
  color: #cbd5e1;
}
.dl-rank-badge.is-bronze {
  background: rgba(251, 146, 60, 0.1);
  color: #fed7aa;
}
.dl-rank-body {
  min-width: 0;
  flex: 1;
}
.dl-rank-name {
  display: block;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dl-rank-bar {
  margin-top: 4px;
  display: block;
  height: 6px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
}
.dl-rank-bar-fill {
  display: block;
  height: 100%;
  border-radius: 9999px;
  background-color: #e2e8f0;
}
.dl-rank-count {
  text-align: right;
  flex-shrink: 0;
}
.dl-rank-count strong {
  display: block;
  font-size: 14px;
  line-height: 20px;
}
.dl-rank-count-unit {
  font-size: 10px;
  color: #64748b;
}
.dl-arrow {
  color: #475569;
  flex-shrink: 0;
}

/* ===== 系统镜像 ===== */
.dl-mirror-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.dl-mirror-card {
  display: block;
  border-radius: 16px;
  background: #101d31;
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 16px;
  transition: background 0.2s;
}
.dl-mirror-card:hover,
.dl-mirror-card:active {
  background: #142943;
}
.dl-mirror-card-top {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dl-mirror-card-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}
.dl-mirror-logo {
  width: 40px;
  height: 40px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: rgba(96,165,250,.1);
  color: #93c5fd;
}
.dl-mirror-name {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dl-mirror-meta {
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
}
.dl-mirror-nums {
  text-align: right;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}
.dl-mirror-nums .rotate-180 {
  transform: rotate(180deg) !important;
  position: absolute;
  top: 3px;
  right: 0;
}
.dl-mirror-nums strong {
  font-size: 16px;
  line-height: 24px;
}
.dl-mirror-delta {
  margin-top: 4px;
  font-size: 11px;
  color: #6ee7b7;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

/* ===== PC 端（≥768px）：居中宽栏 + 多列网格 ===== */
@media (min-width: 768px) {
  .dl-root {
    padding: 40px 24px;
    align-items: flex-start;
  }
  .dl-home {
    max-width: 880px;
    min-height: auto;
    height: auto;
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.07);
    box-shadow: 0 40px 90px -20px rgba(0, 0, 0, 0.6);
  }
  .dl-scroll {
    overflow: visible;
  }
  .dl-header {
    padding: 24px 32px 20px;
  }
  .dl-page {
    padding: 24px 32px 36px;
  }
  .dl-toast {
    bottom: 32px;
  }
  /* 下载方式分布：双列 */
  .dl-channel-panel {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 32px;
    padding: 20px 24px;
  }
  /* 统计卡片间距放大 */
  .dl-stat-grid {
    gap: 16px;
  }
}

/* ===== 大屏 PC（≥1200px）：进一步放宽 ===== */
@media (min-width: 1200px) {
  .dl-root {
    padding: 56px 32px;
  }
  .dl-home {
    max-width: 960px;
  }
  .dl-page {
    padding: 28px 40px 44px;
  }
  .dl-header {
    padding: 28px 40px 24px;
  }
}
</style>



// WEBPACK FOOTER //
// src/view/hipc/SoftwareDownloads.vue